import { useState } from "react";
import ScoreBadge from "./ScoreBadge";
import { stripInterrogatorTags, extractFlagMarkers } from "../lib/stripTags";
import { api } from "@/lib/api";

const PERSONA_STYLES = {
  DIRECTOR: "persona-director",
  "LEAN VP": "persona-leanvp",
  VC: "persona-vc",
};

function segmentByPersona(text) {
  const re = /(\[(DIRECTOR|LEAN VP|VC)\])/gi;
  const parts = [];
  let lastIndex = 0;
  let m;
  let currentLabel = null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push({ label: currentLabel, content: text.slice(lastIndex, m.index) });
    }
    currentLabel = m[2].toUpperCase();
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) {
    parts.push({ label: currentLabel, content: text.slice(lastIndex) });
  }
  if (parts.length === 0) return [{ label: null, content: text }];
  return parts;
}

const RING_TITLE = {
  1: "Interrogator · Ring 1 · Concept & Viability",
  2: "Interrogator · Ring 2 · Marketing & Financial Practicality",
  3: "Interrogator · Ring 3 · Deep Trench Obstacles",
  4: "Growth Architect · Ring 4 · Building the Plan",
};

function FlagButton({ ideaId, ring, figure, sourceQuote, alreadyFlagged }) {
  const [state, setState] = useState(alreadyFlagged ? "flagged" : "idle");
  const label =
    state === "flagged"
      ? "Flagged"
      : state === "sending"
      ? "Flagging…"
      : "Flag figure as unsupported";
  async function flag() {
    if (state !== "idle") return;
    setState("sending");
    try {
      const resp = await api.post(`/ideas/${ideaId}/flag-figure`, {
        figure,
        source_quote: sourceQuote,
        ring,
      });
      setState(resp.data?.already_flagged ? "flagged" : "flagged");
    } catch {
      setState("error");
      setTimeout(() => setState("idle"), 2500);
    }
  }
  return (
    <button
      type="button"
      onClick={flag}
      disabled={state !== "idle"}
      data-testid={`flag-figure-btn-${ring}`}
      className={`mono-caps text-[0.65rem] px-2 py-1 border rounded transition-colors ${
        state === "flagged"
          ? "border-amber-300 bg-amber-50 text-amber-800 cursor-default"
          : state === "error"
          ? "border-red-300 bg-red-50 text-red-800"
          : "border-slate-300 text-slate-600 hover:bg-slate-50"
      }`}
      title={`Figure: ${figure}`}
    >
      {label} · {figure}
    </button>
  );
}

export default function Message({ message, ideaId, flaggedFigures = [] }) {
  if (message.role === "user") {
    const clean = (message.content || "").replace(/^\[INITIAL IDEA\]\s*/i, "");
    return (
      <div className="chat-message user" data-testid="message-user">
        <div className="mono-caps text-slate-500 mb-2">Founder</div>
        <div className="text-slate-900">{clean}</div>
      </div>
    );
  }

  const raw = stripInterrogatorTags(message.content);
  const segments = message.ring === 3 ? segmentByPersona(raw) : [{ label: null, content: raw }];
  const markers = extractFlagMarkers(message.content);
  const flaggedKeys = new Set(
    flaggedFigures
      .filter((f) => f.ring === message.ring)
      .map((f) => (f.figure || "").toLowerCase()),
  );

  return (
    <div className="chat-message assistant" data-testid="message-assistant">
      <div className="mono-caps text-slate-500 mb-3">
        {RING_TITLE[message.ring] || `Interrogator · Ring ${message.ring}`}
      </div>
      <div className="space-y-3 text-slate-800">
        {segments.map((s, i) => (
          <div key={`${message.id}-seg-${i}`}>
            {s.label && (
              <span
                className={`${PERSONA_STYLES[s.label] || ""} mr-2`}
                data-testid={`persona-${s.label.toLowerCase().replace(" ", "-")}`}
              >
                {s.label === "VC" ? "VC" : s.label === "DIRECTOR" ? "DIRECTOR" : "LEAN VP"}
              </span>
            )}
            <span>{s.content.replace(/^:\s*/, "").trim()}</span>
          </div>
        ))}
      </div>
      {markers.length > 0 && ideaId && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="flag-figure-strip">
          {markers.map((mk, idx) => (
            <FlagButton
              key={`${message.id}-flag-${idx}`}
              ideaId={ideaId}
              ring={message.ring}
              figure={mk.figure}
              sourceQuote={mk.reason || raw.slice(0, 400)}
              alreadyFlagged={flaggedKeys.has(mk.figure.toLowerCase())}
            />
          ))}
        </div>
      )}
      {message.score && (
        <div className="mt-4">
          <ScoreBadge score={message.score} />
        </div>
      )}
    </div>
  );
}
